import React, { useMemo } from 'react';
import Workspace from '../../components/Workspace/Workspace';
import { SalesCreditLine, SalesInvoiceLine } from '../../graphql/entities';
import Formatters from '../../components/grid-addons/formatters';
import getProductDP from '../../utils/getProductDP';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { Direction, FilterOperation, ISalesCreditLine, ProductClass } from '../../graphql/schema/p4types';
import { Paths } from '../../@types/keyOfNested';
import { IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import SalesOrderFormModalFormatter from '../../components/modals/formatters/SalesOrderFormModalFormatter';
import { usePrecision } from '../../utils/hooks';
import SalesCreditFormModalFormatter from '../../components/modals/formatters/SalesCreditFormModalFormatter';
import ProductFormModalFormatter from '../../components/modals/formatters/ProductFormModalFormatter';
import CustomerFormModalFormatter from '../../components/modals/formatters/CustomerFormModalFormatter';
import { useAbstractSalesLineColumnOverrides } from '../../utils/abstractSalesHooks';

const { formUrl, type, title, workspace: workspaceQuery } = SalesCreditLine;

const SALES_CREDIT_LINE_FIELD_NAMES: Array<Paths<ISalesCreditLine>> = [
  'salesCredit.documentNumber',
  'salesOrderLine.salesOrder.orderNumber',
  'product.description',
  'salesCredit.customer.name',
  'createdAt',
  'grossSellingPrice',
  'quantity',
  'activeSellingUnit.description',
  'nettLineValue',
];

const getPricePrecisionColumnOverrides = (
  costPricePrecision: number,
  sellingPricePrecision: number
): IWorkspaceColumnOverride => {
  return {
    grossSellingPrice: {
      name: 'Selling Price',
      options: { format: { decimals: sellingPricePrecision } },
    },
    singleUnitGrossSellingPrice: {
      name: 'Single Unit Selling Price',
      options: { format: { decimals: sellingPricePrecision } },
    },
    standardUnitGrossSellingPrice: {
      name: 'Standard Unit Selling Price',
      options: { format: { decimals: sellingPricePrecision } },
    },
    nettSellingPrice: {
      name: 'Nett Selling Price',
      options: { format: { decimals: sellingPricePrecision } },
    },
    singleUnitNettSellingPrice: {
      name: 'Single Unit Nett Selling Price',
      options: { format: { decimals: sellingPricePrecision } },
    },
    profitValue: {
      name: 'Profit Value',
      options: { format: { decimals: sellingPricePrecision } },
    },
    singleUnitProfitValue: {
      name: 'Single Unit Profit Value',
      options: { format: { decimals: sellingPricePrecision } },
    },
    haggleProfitValue: {
      name: 'Haggle Profit Value',
      options: { format: { decimals: sellingPricePrecision } },
    },
    singleUnitHaggleProfitValue: {
      name: 'Single Unit Haggle Profit Value',
      options: { format: { decimals: sellingPricePrecision } },
    },
    costPrice: {
      name: 'Cost Price',
      options: { format: { decimals: costPricePrecision } },
    },
    singleUnitCostPrice: {
      name: 'Single Unit Cost Price',
      options: { format: { decimals: costPricePrecision } },
    },
    haggleCostPrice: {
      name: 'Haggle Cost',
      options: { format: { decimals: costPricePrecision } },
    },
    singleUnitHaggleCostPrice: {
      name: 'Single Unit Haggle Cost',
      options: { format: { decimals: costPricePrecision } },
    },
  };
};

const SalesCreditLineWorkspace = (props) => {
  const costPricePrecision = usePrecision('cost');
  const sellingPricePrecision = usePrecision('sales');
  const columnOverrides: IWorkspaceColumnOverride = useMemo(
    () => ({
      'salesCredit.documentNumber': {
        name: 'Number',
        key: 'salesCredit.documentNumber',
        width: 100,
        formatter: SalesCreditFormModalFormatter,
        options: {
          entityId: 'salesCredit.id',
        },
      },
      'salesOrderLine.salesOrder.orderNumber': {
        name: 'Order Number',
        key: 'salesOrderLine.salesOrder.orderNumber',
        width: 100,
        formatter: SalesOrderFormModalFormatter,
        options: {
          entityId: 'salesOrderLine.salesOrder.id',
        },
      },
      'product.description': {
        key: 'product.id',
        formatter: ProductFormModalFormatter,
        options: {
          entityId: 'product.id',
        },
      },
      'salesCredit.customer.name': {
        name: 'Customer Name',
        key: 'customer.id',
        formatter: CustomerFormModalFormatter,
        options: {
          entityId: 'salesCredit.customer.id',
        },
      },
      createdAt: {
        name: 'Created At',
        width: 150,
      },
      quantity: {
        formatter: Formatters.NumericFormatter,
        cellClass: 'rightAlign',
        options: {
          format: { decimals: getProductDP },
        },
      },
      'product.mainSupplierDetails.supplier.reference': {
        name: 'Main Supplier - Reference',
        sortable: false,
      },
      'product.mainSupplierDetails.supplier.name': {
        name: 'Main Supplier - Name',
        sortable: false,
      },
      'product.weight': {
        name: 'Product - Unit Weight',
      },
      costPrice: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      baseLineCost: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      haggleCostPrice: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      haggleLineCost: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      lineCost: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      singleUnitCostPrice: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      singleUnitHaggleCostPrice: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      nettSellingPrice: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: sellingPricePrecision },
        },
      },
      nettLineValue: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: sellingPricePrecision },
        },
      },
      singleUnitGrossSellingPrice: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: sellingPricePrecision },
        },
      },
      singleUnitNettSellingPrice: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: sellingPricePrecision },
        },
      },
      grossSellingPrice: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: sellingPricePrecision },
        },
      },
      ...getPricePrecisionColumnOverrides(costPricePrecision, sellingPricePrecision),
    }),
    [costPricePrecision, sellingPricePrecision]
  );

  const lineColumnOverrides = useAbstractSalesLineColumnOverrides(
    columnOverrides,
    costPricePrecision,
    sellingPricePrecision
  );

  const workspaceProps = useWorkspace<ISalesCreditLine>(props, SalesCreditLine, {
    columns: SALES_CREDIT_LINE_FIELD_NAMES,
    columnOverrides: lineColumnOverrides,
    initialSortColumn: `salesCredit.documentNumber`,
    initialSortDirection: Direction.Desc,
    filters: [
      {
        filterOperation: FilterOperation.NotEquals,
        filterParameters: {
          field: 'product.productClass',
          value: ProductClass.FreeType,
        },
      },
    ],
  });

  return (
    <Workspace
      entityType={type}
      title={title}
      formUrl={formUrl}
      defaultQuery={workspaceQuery}
      {...workspaceProps}
      {...props}
      entity={SalesCreditLine}
      formIdOverride={`salesCredit.id`}
    />
  );
};

export default SalesCreditLineWorkspace;
